import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from '@mui/material';
import * as Yup from 'yup';
import { shareResource, shareMultipleMarkingTools } from '../../service';
import ActionPopup from '../ActionPopup';
import {
  MainContainer,
  Header,
  HeaderContent,
  ShareIcon,
  Title,
  Subtitle,
  CloseButton,
  DialogBody,
  ItemBox,
  ItemsContainer,
  ItemInfo,
  ItemIcon,
  ItemContent,
  ItemDetails,
  ItemTitle,
  ItemSubtitle,
  ItemTypeLabel,
  ItemActions,
  ItemRemoveButton,
  ItemRemoveIcon,
  SectionLabel,
  EmailInputContainer,
  EmailInput,
  ErrorMessage,
  SelectedEmailsContainer,
  EmailInitials,
  EmailCloseIcon,
  ShareButton,
  AddButton,
} from './style';
import CloseIcon from '../../static/img/closecircle24gray.svg';
import CloseIconSmall from '../../static/icons/red-cross.svg';
import RedCrossIcon from '../../static/icons/red_close.svg';
import ShareIconSVG from '../../static/img/24grayshare.svg';
import MailIcon from '../../static/icons/mail-icon.svg';
import RubricsIcon from '../../static/img/Rubricsnew.svg';
import AddIcon from '../../static/icons/add-icon.svg';

const getShareTypeLabel = (shareType, type) => {
  if (shareType === 'commentBank') {
    return 'Comment Bank';
  }
  if (shareType === 'markingCriteria') {
    return 'Rubric';
  }
  if (shareType === 'successCriteria') {
    return 'Success Criterias';
  }
  if (shareType === 'task') {
    return 'Task';
  }
  return type || 'Resource';
};

const formatItems = (items) => {
  if (!items) {
    return [];
  }
  
  const itemsArray = Array.isArray(items) ? items : [items];
  
  if (itemsArray.length === 0) {
    return [];
  }

  return itemsArray
    .filter((item) => {
      if (!item) return false;
      const hasId = !!item.id;
      const hasShareType = !!item.shareType;
      return hasId && hasShareType;
    })
    .map((item) => ({
      id: item.id,
      shareType: item.shareType,
      name: item?.name || item?.title || 'Untitled',
      subtitle: item?.subtitle || item?.subject || item?.description || '',
      typeLabel: getShareTypeLabel(item?.shareType, item?.type),
      iconType: item?.type || item?.category || item?.shareType,
    }));
};

const getIconByType = (type) => {
  return RubricsIcon;
};

const ShareResourcePopup = ({ 
  open, 
  onClose, 
  items = [], 
  onShare 
}) => {
  const [emailInput, setEmailInput] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [selectedItems, setSelectedItems] = useState(() => formatItems(items));
  const prevOpenRef = useRef(open);
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [actionPopupMessage, setActionPopupMessage] = useState('');
  const [actionPopupType, setActionPopupType] = useState('success');

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedItems(formatItems(items));
      setSelectedEmails([]);
      setEmailInput('');
      setEmailError('');
    }
    prevOpenRef.current = open;
  }, [open, items]);

  const emailSchema = Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required');

  const getInitialsFromEmail = (email) => {
    const namePart = email.split('@')[0];
    const parts = namePart.split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return namePart.substring(0, 2).toUpperCase();
  };

  const handleAddEmail = async () => {
    try {
      await emailSchema.validate(emailInput);
      
      if (selectedEmails.includes(emailInput)) {
        setEmailError('Email already added');
        return;
      }
      
      setSelectedEmails([...selectedEmails, emailInput]);
      setEmailInput('');
      setEmailError('');
    } catch (error) {
      setEmailError(error.message);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleEmailRemove = (emailToRemove) => {
    setSelectedEmails((prev) => prev.filter((email) => email !== emailToRemove));
  };

  const handleItemRemove = (itemId) => {
    setSelectedItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleShare = async () => {
    if (selectedEmails.length === 0 || selectedItems.length === 0) return;

    setIsSharing(true);
    try {
      const itemsPayload = selectedItems.map((item) => ({
        id: item.id,
        shareType: item.shareType,
      }));

      if (itemsPayload.length === 0) {
        setActionPopupMessage('No items to share');
        setActionPopupType('reject');
        setShowActionPopup(true);
        setIsSharing(false);
        return;
      }

      let response = null;

      if (itemsPayload.length > 1) {
        response = await shareMultipleMarkingTools(
          itemsPayload,
          selectedEmails
        );
      } else {
        const [itemToShare] = itemsPayload;
        response = await shareResource(
          [itemToShare.id],
          itemToShare.shareType,
          selectedEmails
        );
      }

      if (response !== null) {
        if (response.errorMessage) {
          setActionPopupMessage(response.errorMessage);
          setActionPopupType('reject');
          setShowActionPopup(true);
        } else if (response.successMessage) {
          setSelectedEmails([]);
          setEmailInput('');
          setActionPopupMessage(response.successMessage);
          setActionPopupType('success');
          setShowActionPopup(true);
          if (onShare) {
            onShare(selectedEmails, response.successMessage);
          }
        } else {
          setSelectedEmails([]);
          setEmailInput('');
          if (onShare) {
            onShare(selectedEmails, null);
          }
          onClose();
        }
      }
    } catch (error) {
      console.error('Share error:', error);
      const errorMessage = error?.message || 'Failed to share. Please try again.';
      setActionPopupMessage(errorMessage);
      setActionPopupType('reject');
      setShowActionPopup(true);
    } finally {
      setIsSharing(false);
    }
  };

  const handleActionPopupClose = () => {
    setShowActionPopup(false);
    if (actionPopupType === 'success') {
      onClose();
    }
  };

  const getAvatarColor = (index) => {
    const colors = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EC4899'];
    return colors[index % colors.length];
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        disableAutoFocus
        disableEnforceFocus
        onClick={(e) => e.stopPropagation()}
      >
        <MainContainer onClick={(e) => e.stopPropagation()}>
          <Header>
            <HeaderContent>
              <ShareIcon src={ShareIconSVG} alt="Share" />
              <Title>Share</Title>
            </HeaderContent>
            <CloseButton src={CloseIcon} onClick={onClose} />
          </Header>
          
          <DialogBody>
            <Subtitle>Share directly with other teachers to save them time</Subtitle>
            
            {selectedItems.length > 0 && (
              <ItemsContainer onClick={(e) => e.stopPropagation()}>
                {selectedItems.map((item) => {
                  const iconSource = getIconByType(item.iconType);

                  return (
                    <ItemBox key={item.id}>
                      <ItemIcon>
                        <img src={iconSource} alt={item.typeLabel} width="48" height="48" />
                      </ItemIcon>
                      <ItemInfo>
                        <ItemContent>
                          <ItemDetails>
                            <ItemTitle>{item.name}</ItemTitle>
                            {item.subtitle && <ItemSubtitle>{item.subtitle}</ItemSubtitle>}
                            <ItemTypeLabel>{item.typeLabel}</ItemTypeLabel>
                          </ItemDetails>
                          <ItemActions>
                            <ItemRemoveButton
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemRemove(item.id);
                              }}
                            >
                              <ItemRemoveIcon src={CloseIconSmall} alt="Remove item" />
                            </ItemRemoveButton>
                          </ItemActions>
                        </ItemContent>
                      </ItemInfo>
                    </ItemBox>
                  );
                })}
              </ItemsContainer>
            )}

            <SectionLabel>
              Select people you want to share with
            </SectionLabel>
            
            <EmailInputContainer hasError={!!emailError} onClick={(e) => e.stopPropagation()}>
              <img src={MailIcon} alt="Mail" width="24" height="24" />
              <EmailInput
                type="email"
                value={emailInput}
                onChange={(e) => {
                  e.stopPropagation();
                  setEmailInput(e.target.value);
                  setEmailError('');
                }}
                onKeyPress={(e) => {
                  e.stopPropagation();
                  handleKeyPress(e);
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="example@email.com"
                hasError={!!emailError}
              />
              <AddButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddEmail();
                }}
                disabled={!emailInput.trim()}
              >
                <img src={AddIcon} alt="Add" />
              </AddButton>
            </EmailInputContainer>
            {emailError && <ErrorMessage>{emailError}</ErrorMessage>}

            {selectedEmails.length > 0 && (
              <SelectedEmailsContainer onClick={(e) => e.stopPropagation()}>
                {selectedEmails.map((email, index) => (
                  <EmailInitials key={email} color={getAvatarColor(index)}>
                    {getInitialsFromEmail(email)}
                    <EmailCloseIcon 
                      src={RedCrossIcon} 
                      alt="Remove" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEmailRemove(email);
                      }}
                    />
                  </EmailInitials>
                ))}
              </SelectedEmailsContainer>
            )}

            <ShareButton 
              onClick={(e) => {
                e.stopPropagation();
                handleShare();
              }} 
              disabled={selectedEmails.length === 0 || selectedItems.length === 0 || isSharing}
            >
              {isSharing ? 'Sharing...' : 'Share'}
            </ShareButton>
          </DialogBody>
        </MainContainer>
      </Dialog>
      <ActionPopup
        open={showActionPopup}
        onClose={handleActionPopupClose}
        type={actionPopupType}
        message={actionPopupMessage}
        primaryButtonLabel={actionPopupType === 'success' ? 'Continue' : 'OK'}
        primaryButtonAction={handleActionPopupClose}
      />
    </>
  );
};

export default ShareResourcePopup;
